import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  exposureStatusLabel,
  exposureValueLabel,
  exposureValueText,
  formatCount,
  formatNumberish,
  type AssetExposureBlock,
} from './layerStatsFormat';
import {
  FeatureFocusButton,
  LocationFocusButton,
  useFocusExternalFeature,
} from './mapFocus';

/** Which org assets sit inside or near one Analytics pack's features. */
export default function AssetExposureTable({
  block,
  layerId,
  layerName,
}: {
  block: AssetExposureBlock;
  layerId: string;
  layerName: string;
}) {
  const focusFeature = useFocusExternalFeature();
  const showValue =
    Boolean(block.valueField) || block.rows.some((row) => row.valueLabel);
  return (
    <Card size="sm">
      <CardHeader className="tw:border-b tw:border-border tw:pb-(--card-spacing)">
        <CardTitle className="tw:text-sm">
          Asset exposure
          <span className="tw:ml-2 tw:text-xs tw:font-normal tw:text-muted-foreground">
            {layerName}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="tw:space-y-2">
        <p className="tw:text-xs">
          <span className="tw:font-semibold">
            {formatCount(block.exposedCount)}
          </span>{' '}
          of {formatCount(block.assetCount)} assets inside or within{' '}
          {formatNumberish(block.radiusKm)} km
        </p>
        {block.rows.length > 0 && (
          <div className="tw:min-w-0 tw:w-full tw:overflow-hidden tw:rounded-md tw:[&_[data-slot=table-container]]:overflow-x-hidden tw:[&_[data-slot=table-container]]:bg-transparent">
            <Table className="tw:table-fixed tw:[&_[data-slot=table-cell]]:truncate">
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead className="tw:w-20">Status</TableHead>
                  {showValue && (
                    <TableHead>
                      {exposureValueLabel(block.valueField)}
                    </TableHead>
                  )}
                  <TableHead className="tw:w-16">
                    <span className="tw:sr-only">Map</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {block.rows.map((row) => (
                  <TableRow key={row.resourceSetId}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          row.status === 'clear' ? 'outline' : 'destructive'
                        }
                        className="tw:text-[10px]"
                      >
                        {exposureStatusLabel(row)}
                      </Badge>
                    </TableCell>
                    {showValue && (
                      <TableCell className="tw:tabular-nums">
                        {exposureValueText(row)}
                      </TableCell>
                    )}
                    <TableCell className="tw:overflow-visible">
                      <div className="tw:flex tw:items-center tw:gap-0.5">
                        <LocationFocusButton
                          location={row.assetLocation}
                          label={`Zoom to ${row.name} on map`}
                        />
                        <FeatureFocusButton
                          label={`Zoom to the ${layerName} feature on map`}
                          disabled={
                            row.featureObjectId == null ||
                            row.featureObjectId === ''
                          }
                          onClick={() =>
                            focusFeature({
                              layerId,
                              objectId: row.featureObjectId,
                              title: row.featureName ?? layerName,
                              lat: row.featureLocation?.lat,
                              lon: row.featureLocation?.lon,
                            })
                          }
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {block.notes.map((n) => (
          <p key={n} className="tw:text-[11px] tw:text-muted-foreground">
            {n}
          </p>
        ))}
      </CardContent>
    </Card>
  );
}
